import React from 'react';
import {
    Topbar,
    CalendarModal,
} from '@components';
import { CalendarFilled } from '@assets';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PUBLIC_ROUTE } from '@router/route.constants';
import {
    LIMITS,
    CHAT_SK,
} from '@constants';
import { useEffect } from 'react';
import { api } from '@api';
import { message } from 'antd';
import { getFingerprintData } from '@utils';
import { useCallback } from 'react';
import Timeline from './Timeline';
import { useRef } from 'react';
import {
    RootContainer,
} from './Message.style';
import { useTranslation } from 'react-i18next';
import SocialsBox from './SocialsBox/SocialsBox';
import ContactsBox from './ContactsBox/ContactsBox';
import OptionsBox from './OptionsBox/OptionsBox';

const { DEFAULT_PAGE, PAGE_SIZE } = LIMITS;

const apiDateFormat = "YYYY-MM-DD";

const Message = () => {
    const { t } = useTranslation()
    const timelineRef = useRef();
    const optRef = useRef();
    const navigate = useNavigate();
    const [roomId, setRoomId] = useState(null);
    const [open, setOpen] = useState(false);
    const [date, setDate] = useState(null);
    const [page, setPage] = useState(DEFAULT_PAGE);
    const [data, setData] = useState([]);
    const [disabled, setDisabled] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(true);

    // Post request states
    const [tempMsg, setTempMsg] = useState([]);

    // styles
    const [optHeight, setOptHeight] = useState(0);

    // device info
    const [device_info, setDeviceInfo] = useState('');

    useEffect(() => {
        getFingerprintData()
            .then(device_info => {
                setDeviceInfo(device_info);
                getRoomId(device_info);
            })
            .catch(() => { });

        // eslint-disable-next-line
    }, []);

    const getRoomId = (device_info) => {
        api
            .getRooms({
                device_info,
            })
            .then(({ rooms }) => {
                let room_id = rooms.filter((item) => item.type === CHAT_SK)[0].room_id;
                setRoomId(room_id);
            })
            .catch((errorMsg) => {
                message.error(errorMsg);
            });
    }

    const getMessages = useCallback((device_info) => {
        setLoading(true);
        let payload = {
            page,
            limit: PAGE_SIZE,
            room_id: roomId,
            device_info,
        };
        if (date) {
            payload.start_date = date;
            payload.end_date = date;
        }
        api
            .getMessages(payload)
            .then(({ messages, pagination: { total } }) => {
                setLoading(false);
                setHasMore(messages.length > 0 && messages.length !== total);
                if (page === 1) {
                    setData(messages.reverse());
                } else {
                    setData((prev) => [...prev, ...messages.reverse()]);
                }
            })
            .catch((errorMsg) => {
                message.error(errorMsg);
            });
    }, [page, roomId, date]);

    useEffect(() => {
        if (roomId) {
            getFingerprintData()
                .then(device_info => {
                    getMessages(device_info);
                })
                .catch(() => { });
        }
    }, [getMessages, roomId]);

    const onOk = (newdate) => {
        newdate = newdate.format(apiDateFormat);
        if (newdate !== date) {
            setDate(newdate);
            setPage(DEFAULT_PAGE);
            setData([]);
            setHasMore(false);
        }
        handleClose();
    }

    const onReset = () => {
        if (date) {
            setDate('');
            setPage(DEFAULT_PAGE);
            setData([]);
            setHasMore(false);
            scrollToBottom();
        }
    }

    const handleClose = () => {
        setOpen(false);
    }

    const goBack = () => {
        navigate(`/${PUBLIC_ROUTE.HOME}`);
    }

    const scrollToBottom = () => {
        setDisabled(true);
        var objDiv = document.getElementById("scrollableDiv");
        if (objDiv) {
            objDiv.scrollTop = objDiv.scrollHeight;
        }
        timelineRef.current.scrollTo(0, timelineRef.current.scrollHeight);

        setTimeout(() => {
            setDisabled(false);
        }, 200);
    };

    // Post request callbacks

    const loadingCallback = (item) => {
        setTempMsg(prev => [item, ...prev]);
    }

    const sendOptionSuccessCallback = ({ reply_msg, sent_msg }) => {
        setTempMsg(prev => prev.filter(v => v.uuid !== sent_msg.uuid));
        setData(prev => [reply_msg, sent_msg, ...prev]);
    }

    const errorCallback = (item) => {
        setTempMsg(prev => prev.map(v => {
            if (v.uuid === item.uuid) {
                return {
                    ...item,
                    failed: true,
                };
            }
            return v;
        }));
    }

    const resetTempData = () => {
        setTempMsg(prev => prev.filter(item => !item.failed));
    }

    const changeOptHeight = () => {
        setOptHeight(optRef.current?.offsetHeight);
    }

    return (
        <>
            <Topbar
                title={t("message.messageTitle")}
                rightIcon={
                    <CalendarFilled onClick={() => setOpen(true)} />
                }
                goBack={goBack}
            />

            <RootContainer>
                <Timeline
                    disabled={disabled}
                    innerRef={timelineRef}
                    items={[...tempMsg, ...data]}
                    hasMore={hasMore}
                    fetch={() => setPage(prev => prev + 1)}
                    date={date}
                    loading={loading}
                    optHeight={optHeight}
                    device_info={device_info}
                />
                <OptionsBox
                    optRef={optRef}
                    room_id={roomId}
                    loadingCallback={loadingCallback}
                    errorCallback={errorCallback}
                    resetTempData={resetTempData}
                    scrollToBottom={scrollToBottom}
                    sendOptionSuccessCallback={sendOptionSuccessCallback}
                    changeOptHeight={changeOptHeight}
                />
                <SocialsBox />
                <ContactsBox />
            </RootContainer>

            <CalendarModal
                open={open}
                date={date}
                onOk={onOk}
                onReset={onReset}
                handleClose={handleClose}
            />
        </>
    );
}

export default Message;